import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ChevronDown, Check, Search, MapPin, Globe } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LocationSelector = ({ value, onChange }) => {
  const { t } = useTranslation();

  const [wholeCountry, setWholeCountry] = useState(value?.region === null && value?.city === null && !!value?.country);

  // Country state
  const [countrySearch, setCountrySearch] = useState(value?.country || '');
  const [countries, setCountries] = useState([]);
  const [showCountryDrop, setShowCountryDrop] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(value?.country || '');

  // Region state
  const [regionSearch, setRegionSearch] = useState(value?.region || '');
  const [regions, setRegions] = useState([]);
  const [showRegionDrop, setShowRegionDrop] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(value?.region || '');

  // City state
  const [citySearch, setCitySearch] = useState(value?.city || '');
  const [cities, setCities] = useState([]);
  const [showCityDrop, setShowCityDrop] = useState(false);
  const [selectedCity, setSelectedCity] = useState(value?.city || '');

  // Language state
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(value?.language || '');
  const [showLangDrop, setShowLangDrop] = useState(false);

  const countryRef = useRef(null);
  const regionRef = useRef(null);
  const cityRef = useRef(null);
  const langRef = useRef(null);

  // Close dropdowns on outside click + restore search text
  useEffect(() => {
    const handler = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setShowCountryDrop(false);
        if (selectedCountry) setCountrySearch(selectedCountry);
      }
      if (regionRef.current && !regionRef.current.contains(e.target)) {
        setShowRegionDrop(false);
        if (selectedRegion) setRegionSearch(selectedRegion);
      }
      if (cityRef.current && !cityRef.current.contains(e.target)) {
        setShowCityDrop(false);
        if (selectedCity) setCitySearch(selectedCity);
      }
      if (langRef.current && !langRef.current.contains(e.target)) setShowLangDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedCountry, selectedRegion, selectedCity]);

  // Fetch countries
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/locations/countries`, {
          params: countrySearch ? { search: countrySearch } : {}
        });
        setCountries(data);
      } catch (e) { /* silent */ }
    }, 200);
    return () => clearTimeout(timer);
  }, [countrySearch]);

  // Fetch languages when country selected
  useEffect(() => {
    if (!selectedCountry) { setLanguages([]); setSelectedLanguage(''); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/locations/languages`, {
          params: { country: selectedCountry }
        });
        setLanguages(data.languages || []);
        // Auto-select default language
        const dflt = data.default || (data.languages && data.languages[0]?.code) || 'en';
        setSelectedLanguage(dflt);
        // If only 1 language — auto-select silently, emit onChange
        emitChange(selectedCountry, wholeCountry ? null : selectedRegion, wholeCountry ? null : selectedCity, dflt);
      } catch (e) {
        // Fallback: English
        setLanguages([{ code: 'en', name: 'English' }]);
        setSelectedLanguage('en');
        emitChange(selectedCountry, wholeCountry ? null : selectedRegion, wholeCountry ? null : selectedCity, 'en');
      }
    })();
  }, [selectedCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch regions when country selected
  useEffect(() => {
    if (!selectedCountry) { setRegions([]); return; }
    if (wholeCountry) return;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/locations/regions`, {
          params: { country: selectedCountry, ...(regionSearch ? { search: regionSearch } : {}) }
        });
        setRegions(data);
      } catch (e) { /* silent */ }
    }, 200);
    return () => clearTimeout(timer);
  }, [selectedCountry, regionSearch, wholeCountry]);

  // Fetch cities when region selected
  useEffect(() => {
    if (!selectedCountry || !selectedRegion || wholeCountry) { setCities([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/locations/cities`, {
          params: { country: selectedCountry, region: selectedRegion, ...(citySearch ? { search: citySearch } : {}) }
        });
        setCities(data);
      } catch (e) { /* silent */ }
    }, 200);
    return () => clearTimeout(timer);
  }, [selectedCountry, selectedRegion, citySearch, wholeCountry]);

  // Helper: emit change with all fields including language
  const emitChange = (country, region, city, language) => {
    onChange({ country, region, city, language: language || selectedLanguage });
  };

  const handleWholeCountryToggle = (checked) => {
    setWholeCountry(checked);
    if (checked) {
      setSelectedRegion('');
      setSelectedCity('');
      setRegionSearch('');
      setCitySearch('');
      emitChange(selectedCountry, null, null, selectedLanguage);
    }
  };

  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
    setCountrySearch(country);
    setShowCountryDrop(false);
    setSelectedRegion('');
    setSelectedCity('');
    setRegionSearch('');
    setCitySearch('');
    // Language will be set by the useEffect that fetches languages
  };

  const handleSelectRegion = (region) => {
    setSelectedRegion(region);
    setRegionSearch(region);
    setShowRegionDrop(false);
    setSelectedCity('');
    setCitySearch('');
    emitChange(selectedCountry, region, null, selectedLanguage);
  };

  const handleSelectCity = (city) => {
    setSelectedCity(city);
    setCitySearch(city);
    setShowCityDrop(false);
    emitChange(selectedCountry, selectedRegion, city, selectedLanguage);
  };

  const handleSelectLanguage = (langCode) => {
    setSelectedLanguage(langCode);
    setShowLangDrop(false);
    emitChange(
      selectedCountry,
      wholeCountry ? null : selectedRegion,
      wholeCountry ? null : selectedCity,
      langCode
    );
  };

  // Get language display name
  const getLanguageName = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.name : code.toUpperCase();
  };

  // Build preview text
  const getPreviewText = () => {
    const langSuffix = selectedLanguage ? ` (${selectedLanguage.toUpperCase()})` : '';
    if (wholeCountry && selectedCountry) return selectedCountry + langSuffix;
    if (selectedCity && selectedRegion && selectedCountry) return `${selectedCity}, ${selectedRegion}, ${selectedCountry}${langSuffix}`;
    if (selectedRegion && selectedCountry) return `${selectedRegion}, ${selectedCountry}${langSuffix}`;
    if (selectedCountry) return selectedCountry + langSuffix;
    return '';
  };

  return (
    <div className="space-y-4" data-testid="location-selector">
      {/* Country selector — always visible */}
      <div className="relative" ref={countryRef}>
        <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 block mb-1">
          {t('location.страна')} *
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={countrySearch}
            onChange={(e) => {
              setCountrySearch(e.target.value);
              setShowCountryDrop(true);
              if (e.target.value !== selectedCountry) {
                setSelectedCountry('');
                setSelectedRegion('');
                setSelectedCity('');
                setRegionSearch('');
                setCitySearch('');
                setLanguages([]);
                setSelectedLanguage('');
              }
            }}
            onFocus={() => {
              setShowCountryDrop(true);
              if (selectedCountry && countrySearch === selectedCountry) {
                setCountrySearch('');
              }
            }}
            placeholder={t('location.начните_вводить_страну')}
            className="w-full pl-9 pr-8 py-2.5 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]"
            data-testid="country-search-input"
          />
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        </div>
        {showCountryDrop && countries.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2DDD8] shadow-lg max-h-60 overflow-y-auto" data-testid="country-dropdown">
            {countries.map((c) => (
              <button
                key={c.country}
                onClick={() => handleSelectCountry(c.country)}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F3EE] flex items-center justify-between ${selectedCountry === c.country ? 'bg-emerald-50 text-[#1BB594]' : 'text-slate-700'}`}
              >
                <span className="font-medium">{c.country}</span>
                {selectedCountry === c.country && <Check className="w-4 h-4 text-[#1BB594]" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Language selector — visible when country selected AND has 2+ languages */}
      {selectedCountry && languages.length > 1 && (
        <div className="relative" ref={langRef}>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 block mb-1">
            <Globe className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
            Query language *
          </label>
          <div
            className="relative cursor-pointer"
            onClick={() => setShowLangDrop(!showLangDrop)}
          >
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1BB594]" />
            <div className="w-full pl-9 pr-8 py-2.5 border border-[#E2DDD8] text-sm bg-white">
              {selectedLanguage ? getLanguageName(selectedLanguage) : 'Select language...'}
            </div>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          </div>
          {showLangDrop && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2DDD8] shadow-lg max-h-60 overflow-y-auto">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleSelectLanguage(lang.code)}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F3EE] flex items-center justify-between ${selectedLanguage === lang.code ? 'bg-emerald-50 text-[#1BB594]' : 'text-slate-700'}`}
                >
                  <span className="font-medium">
                    {lang.name}
                    <span className="ml-2 text-xs text-slate-400 uppercase">{lang.code}</span>
                  </span>
                  {selectedLanguage === lang.code && <Check className="w-4 h-4 text-[#1BB594]" />}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-1">
            AI queries will be generated in this language. Choose the language your target audience uses.
          </p>
        </div>
      )}

      {/* Language badge — for single-language countries (auto-selected, non-editable) */}
      {selectedCountry && languages.length === 1 && selectedLanguage && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Globe className="w-3.5 h-3.5 text-[#1BB594]" />
          <span>Query language: <strong>{getLanguageName(selectedLanguage)}</strong></span>
        </div>
      )}

      {/* Checkbox: Entire country — only visible when country selected */}
      {selectedCountry && (
        <label className="flex items-center gap-2 cursor-pointer" data-testid="whole-country-checkbox">
          <input
            type="checkbox"
            checked={wholeCountry}
            onChange={(e) => handleWholeCountryToggle(e.target.checked)}
            className="w-4 h-4 accent-[#1BB594]"
          />
          <MapPin className="w-4 h-4 text-[#1BB594]" />
          <span className="text-sm font-bold text-slate-700">{t('location.вся_страна')}</span>
          <span className="text-xs text-slate-400">{t('location.запросы_без_геопривязки')}</span>
        </label>
      )}

      {/* Region + City — only when country selected and NOT whole country */}
      {selectedCountry && !wholeCountry && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Region selector */}
          <div className="relative" ref={regionRef}>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 block mb-1">
              {t('location.регион_штат')} *
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={regionSearch}
                onChange={(e) => {
                  setRegionSearch(e.target.value);
                  setShowRegionDrop(true);
                  if (e.target.value !== selectedRegion) {
                    setSelectedRegion('');
                    setSelectedCity('');
                    setCitySearch('');
                  }
                }}
                onFocus={() => {
                  setShowRegionDrop(true);
                  if (selectedRegion && regionSearch === selectedRegion) {
                    setRegionSearch('');
                  }
                }}
                placeholder={t('location.начните_вводить_регион')}
                className="w-full pl-9 pr-8 py-2.5 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]"
                data-testid="region-search-input"
              />
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
            {showRegionDrop && regions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2DDD8] shadow-lg max-h-60 overflow-y-auto" data-testid="region-dropdown">
                {regions.map((r) => (
                  <button
                    key={r.region}
                    onClick={() => handleSelectRegion(r.region)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F3EE] flex items-center justify-between ${selectedRegion === r.region ? 'bg-emerald-50 text-[#1BB594]' : 'text-slate-700'}`}
                  >
                    <span className="font-medium">{r.region}</span>
                    {selectedRegion === r.region && <Check className="w-4 h-4 text-[#1BB594]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* City selector */}
          <div className="relative" ref={cityRef}>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 block mb-1">
              {t('location.город')} <span className="text-slate-300">{t('location.необязательно')}</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={citySearch}
                onChange={(e) => {
                  setCitySearch(e.target.value);
                  setShowCityDrop(true);
                  if (e.target.value !== selectedCity) setSelectedCity('');
                }}
                onFocus={() => {
                  setShowCityDrop(true);
                  if (selectedCity && citySearch === selectedCity) {
                    setCitySearch('');
                  }
                }}
                placeholder={selectedRegion ? t('location.выберите_город') : t('location.сначала_выберите_регион')}
                disabled={!selectedRegion}
                className="w-full pl-9 pr-8 py-2.5 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594] disabled:bg-slate-50 disabled:text-slate-400"
                data-testid="city-search-input"
              />
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
            {showCityDrop && cities.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2DDD8] shadow-lg max-h-60 overflow-y-auto" data-testid="city-dropdown">
                {cities.map((c) => (
                  <button
                    key={c}
                    onClick={() => handleSelectCity(c)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F3EE] flex items-center justify-between ${selectedCity === c ? 'bg-emerald-50 text-[#1BB594]' : 'text-slate-700'}`}
                  >
                    <span>{c}</span>
                    {selectedCity === c && <Check className="w-4 h-4 text-[#1BB594]" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview */}
      {getPreviewText() && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200" data-testid="location-preview">
          <MapPin className="w-4 h-4 text-[#1BB594]" />
          <span className="text-sm font-bold text-slate-700">
            {getPreviewText()}
          </span>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
